import placeholderImg from "@assets/images/placeholder255x255.png";
import DialogContentText from "@material-ui/core/DialogContentText";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import ActionDialog from "@saleor/components/ActionDialog";
import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useOnSetDefaultVariant from "@saleor/hooks/useOnSetDefaultVariant";
import useShop from "@saleor/hooks/useShop";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import useBrandUpdate from "@saleor/products/hooks/useBrandUpdate";
import useProductUserType from "@saleor/products/hooks/useProductUserType";
import useTenantUpdate from "@saleor/products/hooks/useTenantUpdate";
import useUserViewUpdate from "@saleor/products/hooks/useUserViewUpdate";
import {
  useProductCreateDeeplink,
  useProductDeleteMutation,
  useProductImageCreateMutation,
  useProductImageDeleteMutation,
  useProductImagesReorder,
  useProductSetAvailabilityForPurchase,
  useProductSetVisibility,
  useProductUpdateMutation,
  useProductVariantBulkDeleteMutation,
  useProductVariantReorderMutation,
  useSimpleProductUpdateMutation,
  useSimpleProductVariantUpdateMutation
} from "@saleor/products/mutations";
import { SimpleProductVariantUpdate } from "@saleor/products/types/SimpleProductVariantUpdate";
import getProductUpdateUrlByUserType from "@saleor/products/utils/getProductUpdateUrlByUserType";
import getProductVariantAddUrlByUserType from "@saleor/products/utils/getProductVariantCreateUrlByUserType";
import getProductVariantEditUrlByUserType from "@saleor/products/utils/getProductVariantEditUrlByUserType";
import getTenantIdByUserType from "@saleor/products/utils/getTenantIdByUserType";
import {
  hasNoError,
  useProductCreateValidation
} from "@saleor/products/utils/productValidations";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useCollectionSearch from "@saleor/searches/useCollectionSearch";
import useProductTagSearch from "@saleor/searches/useProductTagSearch";
import {
  useStaffTenantAssignmentQuery,
  useStaffTenantPrivateAssignmentQuery
} from "@saleor/staff/queries";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@saleor/utils/handlers/metadataUpdateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import { useWarehouseList } from "@saleor/warehouses/queries";
import { warehouseAddPath } from "@saleor/warehouses/urls";
import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAuth } from "../../../auth/AuthProvider";
import { getMutationState, maybe } from "../../../misc";
import ProductUpdatePage from "../../components/ProductUpdatePage";
import { useProductDetails, useProductVariantQuery } from "../../queries";
import { ProductImageCreateVariables } from "../../types/ProductImageCreate";
import { ProductUpdate as ProductUpdateMutationResult } from "../../types/ProductUpdate";
import {
  KAMSProductUrlQueryParams,
  productImageUrl,
  productListUrl,
  productUrl,
  ProductUrlDialog,
  ProductUrlQueryParams,
  productVariantCreatorUrl
} from "../../urls";
import {
  createImageReorderHandler,
  createImageUploadHandler,
  createUpdateHandler,
  createVariantReorderHandler
} from "./handlers";

function getProductDetailsQueryVariables(
  id,
  productsUser: ProductsUser,
  tenantData: any
) {
  return {
    avoidAbsolute: productsUser.isSellerMaster,
    id,
    mallID: tenantData && tenantData.mall && tenantData.mall.id,
    withMall: productsUser.isStoreAdmin
  };
}

interface ProductUpdateProps {
  id: string;
  params: ProductUrlQueryParams | KAMSProductUrlQueryParams;
}

export const ProductUpdate: React.FC<ProductUpdateProps> = ({ id, params }) => {
  const {
    user: { userType }
  } = useAuth();
  const { user: userDetail } = useUser();
  const navigate = useNavigator();
  const notify = useNotifier();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const [validate, setValidator] = React.useState({
    basePrice: null,
    category: null,
    description: null,
    height: null,
    length: null,
    name: null,
    productStatus: null,
    sku: null,
    weight: null,
    width: null
  });

  // Custom hook for determining if user is KAMS, superuser or regular user
  const productsUser = useProductUserType(params);

  // Hook for updating selected KAMS user view
  const [onUserViewChange, userView, userViews] = useUserViewUpdate(
    userDetail,
    params
  );

  const { result: brandsResult } = useBrandPrivateSearch({
    skip: !productsUser.isKAMS,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      accountManager: productsUser.id
    }
  });

  const brands = maybe(() => brandsResult?.data?.search?.edges, []).map(
    edge => edge.node
  );

  const tenantsPrivate = maybe(() => brandsResult?.data?.search?.edges, [])
    .map(edge => edge.node.tenants)
    .flat();

  // Hook for updating of brands
  const [onBrandChange, brand, brandChoices] = useBrandUpdate(
    params,
    brands,
    brandsResult.loading
  );

  // Hook for mapping stores
  const [onTenantChange, tenant, tenantChoices] = useTenantUpdate(
    tenantsPrivate || [],
    brand.value,
    userView.value,
    params
  );

  const intl = useIntl();
  // const hasAccess = hasFullAccess();
  const {
    loadMore: loadMoreCollections,
    search: searchCollections,
    result: searchCollectionsOpts
  } = useCollectionSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreTags,
    search: searchTags,
    result: searchTagsOpts
  } = useProductTagSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const warehouses = useWarehouseList({
    displayLoader: true,
    variables: {
      first: 50
    }
  });
  const shop = useShop();
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const {
    data: tenantNonKamsData,
    loading: tenantNonKamsLoading
  } = useStaffTenantAssignmentQuery({
    skip:
      productsUser.isKAMS ||
      (!productsUser.isKAMS && !productsUser.isStoreAdmin),
    variables: {
      id: getTenantIdByUserType(productsUser, userType, params)
    }
  });

  const {
    data: tenantKamsData,
    loading: tenantKamsLoading
  } = useStaffTenantPrivateAssignmentQuery({
    skip:
      !productsUser.isKAMS ||
      (productsUser.isKAMS && !productsUser.isStoreAdmin),
    variables: {
      id: getTenantIdByUserType(productsUser, userType, params)
    }
  });

  const tenantData = productsUser.isKAMS
    ? tenantKamsData && tenantKamsData.tenantPrivate
    : tenantNonKamsData && tenantNonKamsData.tenant;

  const tenantAssignmentLoading = productsUser.isKAMS
    ? tenantKamsLoading
    : tenantNonKamsLoading;

  const mallId = tenantData && tenantData.mall && tenantData.mall.id;
  // Wait for mall id so visibility radios are not seeded as unpublished, but do
  // not skip forever if the tenant query finishes without a mall.
  const skipProductDetails =
    productsUser.isStoreAdmin &&
    mallId === undefined &&
    tenantAssignmentLoading;

  useEffect(() => {
    const tenantId = getTenantIdByUserType(productsUser, userType, params);
    if (
      productsUser.isStoreAdmin &&
      tenantId &&
      !tenantAssignmentLoading &&
      mallId === undefined
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to load mall details. Product visibility may be incomplete."
        })
      });
    }
  }, [productsUser.isStoreAdmin, tenantAssignmentLoading, mallId]);

  const { data, loading, refetch } = useProductDetails({
    displayLoader: true,
    skip: skipProductDetails,
    variables: getProductDetailsQueryVariables(id, productsUser, tenantData)
  });

  const getID = () => {
    if (data && data.product && data.product.variants.length > 0) {
      return data.product.variants[0].id;
    }
    return "";
  };

  const { data: productVariant } = useProductVariantQuery({
    skip:
      !productsUser.isStoreAdmin || getID() === "" || tenantData === undefined,
    variables: getProductDetailsQueryVariables(
      getID(),
      productsUser,
      tenantData
    )
  });

  const handleUpdate = (data: ProductUpdateMutationResult) => {
    if (data.productUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      refetch();
    } else if (
      data.productUpdate.errors.some(e => e.code === "INVALID") &&
      data.productUpdate.errors.some(e => e.field === "productStatus")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Product cannot be changed to unlisted if product is published in a store."
        })
      });
    } else if (data.productUpdate.errors.some(e => e.code === "INVALID")) {
      notify({
        status: "error",
        text: data.productUpdate.errors[0].message
      });
    }
  };

  const handleProductVariantUpdate = (data: SimpleProductVariantUpdate) => {
    if (data.productVariantStocksCreate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    } else {
      notify({
        status: "error",
        text: data.productVariantStocksCreate.errors[0].message
      });
    }
  };

  const [updateProduct, updateProductOpts] = useProductUpdateMutation({
    onCompleted: handleUpdate
  });
  const [
    updateSimpleProduct,
    updateSimpleProductOpts
  ] = useSimpleProductUpdateMutation({
    onCompleted: handleUpdate
  });

  const [
    simpleProductVariantUpdate /* , simpleProductVariantUpdateOpts*/
  ] = useSimpleProductVariantUpdateMutation({
    onCompleted: handleProductVariantUpdate
  });

  const [
    reorderProductImages,
    reorderProductImagesOpts
  ] = useProductImagesReorder({});

  const [deleteProduct, deleteProductOpts] = useProductDeleteMutation({
    onCompleted: () => {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Product removed"
        })
      });
      navigate(productListUrl());
    }
  });

  const [
    createProductImage,
    createProductImageOpts
  ] = useProductImageCreateMutation({
    onCompleted: data => {
      const imageError = data.productImageCreate.errors.find(
        error => error.field === ("image" as keyof ProductImageCreateVariables)
      );
      if (imageError) {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      } else {
        notify({
          status: "success",
          text: "Product image upload successful"
        });
        refetch();
      }
    }
  });

  const [deleteProductImage] = useProductImageDeleteMutation({
    onCompleted: () => {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      refetch();
    }
  });

  const [
    bulkProductVariantDelete,
    bulkProductVariantDeleteOpts
  ] = useProductVariantBulkDeleteMutation({
    onCompleted: data => {
      if (data.productVariantBulkDelete.errors.length === 0) {
        notify({
          status: "success",
          text: "Variant removed"
        });
        closeModal();
        reset();
        refetch();
      } else if (
        data.productVariantBulkDelete.errors.length !== 0 &&
        data.productVariantBulkDelete.errors[0].field === "variant"
      ) {
        notify({
          status: "error",
          text: "Product must have at least 1 variant"
        });
        closeModal();
        reset();
        refetch();
      } else if (
        data.productVariantBulkDelete.errors.length !== 0 &&
        data.productVariantBulkDelete.errors[0].field === "productVisibility"
      ) {
        notify({
          status: "error",
          text: "Cannot delete a published variant"
        });
        closeModal();
        reset();
        refetch();
      } else {
        notify({
          status: "error",
          text: "Something went wrong"
        });
        closeModal();
        reset();
        refetch();
      }
    }
  });

  const [
    setProductAvailability,
    productAvailabilityOpts
  ] = useProductSetAvailabilityForPurchase({
    onCompleted: data => {
      const errors = data?.productSetAvailabilityForPurchase?.errors;
      if (errors?.length === 0) {
        const updatedProduct = data?.productSetAvailabilityForPurchase?.product;
        setProduct(product => {
          if (!product || !updatedProduct) {
            return product;
          }
          return {
            ...product,
            availableForPurchase: updatedProduct.availableForPurchase,
            isAvailable: updatedProduct.isAvailable,
            isAvailableForPurchase: updatedProduct.isAvailableForPurchase
          };
        });
      }
    }
  });

  const [
    setProductVisibility,
    setProductVisibilityOpts
  ] = useProductSetVisibility({
    onCompleted: data => {
      const errors = data?.setProductVisibility?.productErrors;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Visibility Updated",
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage:
              "Changes cannot be saved, Either product is not ready or not published",
            description: "snackbar text"
          })
        });
      }
      navigate(productUrl(id, params));
    }
  });

  const [createDeeplink, createDeeplinkOpts] = useProductCreateDeeplink({
    onCompleted: data => {
      const errors = data?.productGenerateDynamicLink?.productErrors;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Product Updated",
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Oops, something went wrong",
            description: "snackbar text"
          })
        });
      }
    }
  });

  const handleFileSizeNotify = (type: string) => {
    if (type === "warning") {
      notify({
        status: type,
        text: intl.formatMessage({
          defaultMessage:
            "Some of the image that you are trying to upload is too large."
        }),
        title: intl.formatMessage({
          defaultMessage: "Can't Upload."
        })
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "File size of the image you are trying to upload is too large."
        }),
        title: intl.formatMessage({
          defaultMessage: "Can't Upload."
        })
      });
    }
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    ProductUrlDialog,
    ProductUrlQueryParams
  >(navigate, params => productUrl(id, params), params);

  const handleBack = () => navigate(productListUrl());

  const [product, setProduct] = useStateFromProps(data?.product);

  const {
    loadMore: loadMoreCategories,
    search: searchCategories,
    result: searchCategoriesOpts
  } = useCategorySearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      storeType: product?.productType?.storeType
    }
  });

  if (product === null) {
    return <NotFoundPage onBack={handleBack} />;
  }

  const handleVariantAdd = () =>
    navigate(getProductVariantAddUrlByUserType(productsUser, id, params));
  const handleImageDelete = (id: string) => () =>
    deleteProductImage({ variables: { id } });
  const handleImageEdit = (imageId: string) => () =>
    navigate(productImageUrl(id, imageId));
  const handleSubmit = createMetadataUpdateHandler(
    product,
    createUpdateHandler(
      productsUser.isStoreAdmin,
      product,
      variables => updateProduct({ variables }),
      variables => updateSimpleProduct({ variables }),
      variables => setProductAvailability({ variables }),
      variables => simpleProductVariantUpdate({ variables })
    ),
    variables => updateMetadata({ variables }),
    variables => updatePrivateMetadata({ variables })
  );

  const handleSubmitVisibility = data => {
    const isAvailable =
      product.productVisibility && product.productVisibility.isAvailable
        ? product?.productVisibility?.isAvailable
        : false;
    const isPublish =
      product.productVisibility && product.productVisibility.isVisible
        ? product?.productVisibility?.isVisible
        : false;
    if (
      data.isAvailableForPurchase !== isAvailable ||
      data.isPublished !== isPublish
    ) {
      setProductVisibility({
        variables: {
          input: {
            isAvailable: data.isAvailableForPurchase,
            isVisible: data.isPublished,
            product: id,
            tenant: getTenantIdByUserType(productsUser, userType, params)
          }
        }
      });
    }

    if (data.stockOnMall !== "") {
      const createParams = [
        {
          quantity: data.stockOnMall === "" ? 0 : data.stockOnMall,
          warehouse: tenantData.mall?.firstWarehouse?.id || ""
        }
      ];
      simpleProductVariantUpdate({
        variables: {
          addStocks:
            productVariant.productVariant.stockOnMall === null
              ? createParams
              : [],
          deleteStocks: [],
          productVariantId: product.variants[0].id,
          updateStocks:
            productVariant.productVariant.stockOnMall === null
              ? []
              : createParams
        }
      });
    }

    updateProduct({
      variables: {
        id,
        input: {
          name: product.name,
          nonCod: data.nonCod
        }
      }
    });
  };
  const handleImageUpload = createImageUploadHandler(id, variables =>
    createProductImage({ variables })
  );
  const handleImageReorder = createImageReorderHandler(product, variables =>
    reorderProductImages({ variables })
  );

  const [
    reorderProductVariants,
    reorderProductVariantsOpts
  ] = useProductVariantReorderMutation({});

  const handleVariantReorder = createVariantReorderHandler(product, variables =>
    reorderProductVariants({ variables })
  );

  const disableFormSave =
    createProductImageOpts.loading ||
    deleteProductOpts.loading ||
    reorderProductImagesOpts.loading ||
    updateProductOpts.loading ||
    productAvailabilityOpts.loading ||
    reorderProductVariantsOpts.loading ||
    setProductVisibilityOpts.loading ||
    createDeeplinkOpts.loading ||
    loading ||
    skipProductDetails;

  const formTransitionState = getMutationState(
    updateProductOpts.called || updateSimpleProductOpts.called,
    updateProductOpts.loading || updateSimpleProductOpts.loading,
    maybe(() => updateProductOpts.data.productUpdate.errors),
    maybe(() => updateSimpleProductOpts.data.productUpdate.errors)
    // maybe(() => updateSimpleProductOpts.data.productVariantUpdate.errors)
  );

  const categories = maybe(
    () => searchCategoriesOpts.data.search.edges,
    []
  ).map(edge => edge.node);
  const collections = maybe(
    () => searchCollectionsOpts.data.search.edges,
    []
  ).map(edge => edge.node);
  const tags = maybe(() => searchTagsOpts.data.search.edges, []).map(
    edge => edge.node
  );
  const errors = [
    ...maybe(() => updateProductOpts.data.productUpdate.errors, []),
    ...maybe(() => updateSimpleProductOpts.data.productUpdate.errors, [])
  ];
  const onSetDefaultVariant = useOnSetDefaultVariant(
    product ? product.id : null,
    null
  );

  const handleValidation = data => {
    const variantNoDimensionOnReady = Boolean(
      product &&
        product.variants[0].width === null &&
        data.productStatus === "READY"
    );
    const validationResult = useProductCreateValidation(data);
    setValidator(validationResult);
    if (
      (variantNoDimensionOnReady && !product?.isSimpleProduct) ||
      (variantNoDimensionOnReady && !data.isSimpleProduct)
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Product cannot be ready if the variant has no dimensions",
          description: "snackbar text"
        })
      });
    } else {
      if (hasNoError(validationResult)) {
        handleSubmit(data);
      }
    }
  };

  useEffect(() => {
    if (
      "viewAs" in params &&
      productsUser.isKAMS &&
      userView.value !== params.viewAs
    ) {
      navigate(productListUrl());
    }
  }, [userView.value]);

  useEffect(() => {
    if (
      productsUser.isKAMS &&
      productsUser.isStoreAdmin &&
      tenant.value.tenantId
    ) {
      const updatedParams = {
        ...params,
        mallId: tenant.value.mallId,
        tenant: tenant.value.tenantId
      };
      navigate(getProductUpdateUrlByUserType(productsUser, id, updatedParams));
    }
  }, [tenant.value.tenantId]);

  return (
    <>
      <WindowTitle title={maybe(() => data.product.name)} />
      <ProductUpdatePage
        productsUser={productsUser}
        brand={brand.value}
        brands={brandChoices}
        brandLabel={brand.label}
        onBrandChange={onBrandChange}
        userView={userView.value}
        userViewLabel={userView.label}
        userViews={userViews}
        onUserViewChange={onUserViewChange}
        tenantId={tenant.value.tenantId}
        tenants={tenantChoices}
        tenantLabel={tenant.label}
        onTenantChange={onTenantChange}
        categories={categories}
        collections={collections}
        tags={tags}
        defaultWeightUnit={shop?.defaultWeightUnit}
        disabled={disableFormSave}
        onSetDefaultVariant={onSetDefaultVariant}
        errors={errors}
        fetchCategories={searchCategories}
        fetchCollections={searchCollections}
        fetchTags={searchTags}
        saveButtonBarState={formTransitionState}
        images={maybe(() => data.product.images)}
        header={maybe(() => product.name)}
        placeholderImage={placeholderImg}
        product={product}
        warehouses={
          warehouses.data?.warehouses.edges.map(edge => edge.node) || []
        }
        taxTypes={data?.taxTypes}
        variants={maybe(() => product.variants)}
        onBack={handleBack}
        onDelete={() => openModal("remove")}
        onImageReorder={handleImageReorder}
        onSubmit={data => handleValidation(data)}
        onSubmitVisibility={handleSubmitVisibility}
        onWarehouseConfigure={() => navigate(warehouseAddPath)}
        onVariantAdd={() => {
          handleVariantAdd();
        }}
        onVariantsAdd={() => navigate(productVariantCreatorUrl(id))}
        onVariantShow={variantId => () => {
          navigate(
            getProductVariantEditUrlByUserType(
              productsUser,
              product.id,
              variantId,
              params
            )
          );
        }}
        onVariantReorder={handleVariantReorder}
        onImageUpload={handleImageUpload}
        onImageEdit={handleImageEdit}
        onImageDelete={handleImageDelete}
        tenant={maybe(() => tenantData.mall)}
        toolbar={
          <IconButton
            color="primary"
            onClick={() =>
              openModal("remove-variants", {
                ids: listElements
              })
            }
          >
            <DeleteIcon />
          </IconButton>
        }
        isChecked={isSelected}
        selected={listElements.length}
        stockOnMall={productVariant?.productVariant.stockOnMall}
        toggle={toggle}
        toggleAll={toggleAll}
        fetchMoreCategories={{
          hasMore: maybe(
            () => searchCategoriesOpts.data.search.pageInfo.hasNextPage
          ),
          loading: searchCategoriesOpts.loading,
          onFetchMore: loadMoreCategories
        }}
        fetchMoreCollections={{
          hasMore: maybe(
            () => searchCollectionsOpts.data.search.pageInfo.hasNextPage
          ),
          loading: searchCollectionsOpts.loading,
          onFetchMore: loadMoreCollections
        }}
        fetchMoreTags={{
          hasMore: maybe(() => searchTagsOpts.data.search.pageInfo.hasNextPage),
          loading: searchTagsOpts.loading,
          onFetchMore: loadMoreTags
        }}
        onGenerateDeeplink={id => createDeeplink({ variables: { id } })}
        onFileSizeError={handleFileSizeNotify}
        validation={validate}
      />
      <ActionDialog
        open={params.action === "remove"}
        onClose={closeModal}
        confirmButtonState={deleteProductOpts.status}
        onConfirm={() => deleteProduct({ variables: { id } })}
        variant="delete"
        title={intl.formatMessage({
          defaultMessage: "Delete Product",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to delete {name}?"
            description="delete product"
            values={{
              name: product ? product.name : undefined
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "remove-variants"}
        onClose={closeModal}
        confirmButtonState={bulkProductVariantDeleteOpts.status}
        onConfirm={() =>
          bulkProductVariantDelete({
            variables: {
              ids: params.ids,
              ...(productsUser.isKAMS && { brand: product.brand.id })
            }
          })
        }
        variant="delete"
        title={intl.formatMessage({
          defaultMessage: "Delete Product Variants",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this variant?} other{Are you sure you want to delete {displayQuantity} variants?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};
export default ProductUpdate;
